import { EDesignerEntityKind, ESdChangeReason, IApiManagement, ViewPart } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import {
  hasRecentlyModified,
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  resolveActiveTab,
  selectRecentlyModified
} from "../../../shared/utility/recently-modified";
import { buildLastModifiedColumn } from "../../../shared/utility/recently-modified-columns";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaCodeText from "../../ui-kit/typography/CodeText/CodeText";
import KuikaH4 from "../../ui-kit/typography/H4/H4";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import {
  apiAccessSummary,
  filterApis,
  firstMethodPath,
  IApiAccessSummary,
  IApiMethodBadge,
  methodBadges
} from "./api-builder-model";
import { ApiNameModal } from "./api-name-modal";
import "./api-builder.scss";
import { buildApiRowActions } from "./api-row-actions";

/**
 * The API Builder landing page: the project's APIs as a table, above the card that creates
 * one.
 *
 * It replaces the API's LEFT PANEL, so everything that panel could do lives here — the
 * list, its search and the search's persistence, delete, and both empty states. What the
 * panel also did while an API was OPEN (moving between APIs, starting a new one) is the
 * editor header's switcher, because a landing page is not reachable from inside an editor
 * the way a resident panel was.
 *
 * Methods and Access are both derived from `json.methods` — see `api-builder-model.ts`.
 * `IApiManagement` is id/name/json and carries neither a timestamp nor an author, so when an
 * API was last written, and by whom, comes from the shared entity-modification feed — which is
 * what the "Recently modified" tab the page opens on, and the last two columns, both read.
 *
 * Every mutation belongs to the canvas, which stays mounted while the editor is up; this
 * page only decides what to ask for.
 */

export interface IApiBuilderLandingProps {
  /** True while a create is in flight, so the card can block a second click. */
  isCreating: boolean;
  /**
   * Where the create stands, for the name sheet's confirm. The canvas opens the new API's editor
   * once the ✓ has been seen, and that editor replaces this page — the sheet goes with it. A failed
   * create leaves the sheet open on "Try again".
   */
  createStatus: KuikaButtonStatus;
  /** Id of the row whose delete is in flight. */
  busyApiId?: string;
  /** Starts the create; `createStatus` reports how it goes. */
  onCreate: (name: string) => void;
  onOpenApi: (api: IApiManagement) => void;
  onRequestDelete: (api: IApiManagement) => void;
}

/** The tab holding every API, beside the "Recently modified" one the page opens on. */
const ALL_TAB: string = "All APIs";

interface IApiBuilderLandingState {
  searchText: string;
  isNameModalVisible: boolean;
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
}

class ApiBuilderLanding extends PureSdComponent<IApiBuilderLandingProps & IWithSDProps, IApiBuilderLandingState> {
  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: IApiBuilderLandingProps & IWithSDProps) {
    super(props);
    this.state = {
      searchText: "",
      isNameModalVisible: false,
      pickedTab: null,
      feed: LOADING_FEED
    };
  }

  componentDidMount = (): void => {
    this.isMounted_ = true;
    // The panel rehydrated its query when the tool was re-entered; keep that.
    const searchText: string = this.props.sd.searchTexts[ViewPart.ApiManagementPanel];
    if (searchText && searchText.length > 0) {
      this.setState({ searchText });
    }
    void this.loadFeed();
  };

  componentWillUnmount = (): void => {
    this.isMounted_ = false;
  };

  /**
   * The page stays mounted through a create started elsewhere, so `apiManagements` can gain or
   * lose a row without this page ever remounting to re-fetch the feed on its own. The id set is
   * the smallest correct signal: it fires on a create/delete and stays quiet on an in-place
   * rename, which this page reads off the row itself rather than off the feed.
   */
  componentDidUpdate = (prevProps: Readonly<IApiBuilderLandingProps & IWithSDProps>): void => {
    const previousApis: IApiManagement[] = prevProps.sd?.apiManagements ?? [];
    if (haveItemIdsChanged(previousApis, this.getApis(), (row: IApiManagement) => row.id)) {
      void this.loadFeed();
    }
  };

  /**
   * `IApiManagement` is id/name/json — it carries no timestamp and no author, so when an API was
   * last touched can only come from the entity-modification feed.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const projectId: string | undefined = this.props.sd?.project?.id;
    const feed: IModificationFeed = await loadModificationFeed([EDesignerEntityKind.ApiManagement], projectId);
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  getRecentApis = (): IApiManagement[] =>
    selectRecentlyModified<IApiManagement>({
      items: this.getFilteredApis(),
      index: this.state.feed.index,
      getId: (row: IApiManagement) => row.id,
      getName: (row: IApiManagement) => row.name ?? "",
      now: Date.now()
    });

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to the full list when nothing is recent or the feed failed, and otherwise
   * shows whatever the user picked.
   */
  getActiveTab = (): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      // Asked of every API, not the searched ones: a query that matches nothing recent must not
      // move the user to another tab mid-keystroke.
      hasRecent: hasRecentlyModified<IApiManagement>(
        this.getApis(),
        this.state.feed.index,
        (row: IApiManagement) => row.id,
        Date.now()
      ),
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: ALL_TAB
    });

  handleSearchChange = (searchText: string): void => {
    this.setState({ searchText });
    // Same store key the panel used, so a query survives leaving the tool and coming back.
    this.props.sd.setSearchTexts(ViewPart.ApiManagementPanel, searchText);
  };

  /**
   * `loadApiManagements` writes the same response to both `sd.apiManagements` (which the
   * canvas read) and `project.apiManagements` (which the panel read). The provider's copy
   * is the one to use: it defaults to `[]`, so a `??` fallback to the project's would be
   * unreachable anyway.
   */
  getApis = (): IApiManagement[] => this.props.sd?.apiManagements ?? [];

  getFilteredApis = (): IApiManagement[] => filterApis(this.getApis(), this.state.searchText);

  openNameModal = (): void => {
    this.setState({ isNameModalVisible: true });
  };

  closeNameModal = (): void => {
    this.setState({ isNameModalVisible: false });
  };

  /**
   * The sheet stays up through the whole create: its Create button runs, holds its ✓, and only
   * then does the canvas open the new API's editor, which replaces this page and the sheet with it.
   * It used to close on submit, before the request had even left, and hand the wait to a dimmed
   * page that did not say what it was waiting on.
   */
  handleNameSubmit = (name: string): void => {
    this.props.onCreate(name);
  };

  getColumns = (): KuikaListTableColumn<IApiManagement>[] => [
    {
      id: "name",
      header: () => "Name",
      accessorFn: (row: IApiManagement) => row.name?.toLowerCase() ?? "",
      cell: (row: IApiManagement) => {
        const path: string | undefined = firstMethodPath(row);
        return (
          <span
            className="kuika-api-builder__name-cell"
            // The ids the API list has always emitted for a row, kept for the E2E suite and
            // for the backend-authored onboarding steps anchored to them.
            data-testid={`${row.name}Api`}
            data-onboarding-id={`left-panel_api-management_${row.name}-api`}
            style={
              {
                "--kuika-list-table-icon-bg": "var(--blue-1)",
                "--kuika-list-table-icon-color": "var(--accent-fg)"
              } as React.CSSProperties
            }
          >
            <KuikaListTableNameCell
              name={row.name}
              icon={<KuikaSvg name="api-service" width={14} height={14} />}
              secondary={path && <KuikaCodeText>{path}</KuikaCodeText>}
              secondaryTitle={path}
            />
          </span>
        );
      }
    },
    {
      id: "methods",
      header: () => "Methods",
      width: 170,
      accessorFn: (row: IApiManagement) => apiAccessSummary(row).methodCount,
      cell: (row: IApiManagement) => {
        const count: number = apiAccessSummary(row).methodCount;
        if (count === 0) {
          return <KuikaSmallText className="kuika-list-table__muted">—</KuikaSmallText>;
        }
        const badges: IApiMethodBadge[] = methodBadges(row);
        const remainder: number = count - badges.length;
        return (
          <span className="kuika-api-builder__method-badges">
            {badges.map((badge: IApiMethodBadge) => (
              <KuikaBadge key={badge.key} type="label" size="small" color={badge.color} content={badge.label} />
            ))}
            {remainder > 0 && (
              <KuikaBadge type="label" size="small" variant="subtle" color="grey" content={`+${remainder}`} />
            )}
          </span>
        );
      }
    },
    {
      id: "access",
      header: () => "Access",
      width: 170,
      // Sorted by exposure: the APIs with unauthenticated endpoints come first.
      accessorFn: (row: IApiManagement) => -apiAccessSummary(row).anonymousCount,
      cell: (row: IApiManagement) => {
        const summary: IApiAccessSummary = apiAccessSummary(row);
        // No methods, nothing to expose: the same dash the Methods cell beside it shows, not a gap.
        if (summary.methodCount === 0) {
          return <KuikaSmallText className="kuika-list-table__muted">—</KuikaSmallText>;
        }
        if (summary.anonymousCount === 0) {
          return <KuikaListTableStatusCell label="Protected" color="grey" animateChanges />;
        }
        return (
          <KuikaListTableStatusCell
            label={summary.anonymousCount === 1 ? "1 public" : `${summary.anonymousCount} public`}
            color="orange"
            // The access summary moves with the API's methods; when it does, it morphs in place.
            animateChanges
            hint="Methods that allow anonymous access — anyone can call them without signing in."
          />
        );
      }
    },
    buildLastModifiedColumn<IApiManagement>(this.state.feed.index, (row: IApiManagement) => row.id)
  ];

  getRowActions = (): KuikaListTableRowAction<IApiManagement>[] => buildApiRowActions(this.props.onRequestDelete);

  getEmptyTitle = (isNoResults: boolean, isEmptyRecents: boolean): string => {
    if (isNoResults) return "No API found";
    if (isEmptyRecents) return "Nothing changed recently";
    return "No APIs yet";
  };

  getEmptyDescription = (isNoResults: boolean, isEmptyRecents: boolean): string => {
    if (isNoResults) return "We couldn't find any items matching your search.";
    if (isEmptyRecents) {
      // A failed feed is said out loud rather than shown as "nothing happened", which would be a
      // claim the page cannot actually make.
      return this.state.feed.status === "failed"
        ? "We couldn't load recent activity — open All APIs to see every API."
        : "APIs edited in the last 30 days appear here.";
    }
    return "No APIs have been added yet.";
  };

  renderTable = (): React.ReactNode => {
    const isRecentTab: boolean = this.getActiveTab() === RECENTLY_MODIFIED_TAB;
    const rows: IApiManagement[] = isRecentTab ? this.getRecentApis() : this.getFilteredApis();
    const hasApis: boolean = this.getApis().length > 0;
    const isSearching: boolean = this.state.searchText.length > 0;
    // The "nothing here yet" state wins over the search miss, as it did in the panel.
    const isNoResults: boolean = hasApis && isSearching;
    // The recent tab is only reached with nothing in it when the user picked it deliberately,
    // or when a search emptied it — the page opens on the full list otherwise.
    const isEmptyRecents: boolean = isRecentTab && rows.length === 0 && !isSearching;
    // The page parks on the recent tab while the feed is in flight. Without this it would paint
    // an empty table first and claim nothing had changed recently — something it cannot yet know.
    const isFeedPending: boolean = isRecentTab && this.state.feed.status === "loading";

    return (
      <KuikaListTable<IApiManagement>
        data={rows}
        loading={isFeedPending && rows.length === 0}
        columns={this.getColumns()}
        rowActions={this.getRowActions()}
        // The id the API list has always emitted for a row's menu, kept so the E2E suite
        // still targets one specific API's actions.
        rowActionsTestId={(row: IApiManagement) => `${row.name}ApiThreeDotMenu`}
        rowKey={(row: IApiManagement) => row.id}
        isRowBusy={(row: IApiManagement) => row.id === this.props.busyApiId}
        onRowClick={(row: IApiManagement) => this.props.onOpenApi(row)}
        emptyIcon={<KuikaSvg name={isNoResults ? "search-off" : "api-service"} width={32} height={32} />}
        emptyVariant="page"
        emptyTitle={this.getEmptyTitle(isNoResults, isEmptyRecents)}
        emptyDescription={this.getEmptyDescription(isNoResults, isEmptyRecents)}
        data-testid="apiBuilderTable"
      />
    );
  };

  render() {
    return (
      <>
        <KuikaListPage className="kuika-api-builder" data-testid="apiBuilderScreen">
          {/* Named after the page's job — managing the APIs you already have — not after
              the create card, which is one element within it. */}
          <KuikaListPageHeader
            icon={<KuikaSvg name="api-service" width={20} height={20} />}
            title="API Builder"
            titleTestId="apiBuilderTitle"
            // The API's panel header carried this anchor; onboarding tours are authored in
            // the backend against it, and a step whose target never mounts silently
            // mis-anchors rather than erroring. It moves here with the list.
            renderTitle={(title: string) => (
              <KuikaH4 data-testid="apiBuilderTitle" data-onboarding-id="searchable-panel-header-name-api's">
                {title}
              </KuikaH4>
            )}
          />

          <KuikaCreateCardRow>
            <KuikaCreateCard
              title="Create a blank API"
              description="Turn the application you developed into a data source for your other systems."
              icon={<KuikaSvg name="plus" width={20} height={20} />}
              iconBackground="var(--blue-1)"
              iconColor="var(--accent-fg)"
              onClick={this.openNameModal}
              loading={this.props.isCreating}
              data-testid="newBlankApiButton"
              data-onboarding-id="left-panel_api-management_plus"
            />
          </KuikaCreateCardRow>

          <KuikaListPageToolbar
            tabs={
              <KuikaTabs
                size="lg"
                options={[
                  // `label` is not optional in practice: KuikaTabs renders `icon || label`, so an
                  // option carrying only a value draws an empty pill.
                  {
                    value: RECENTLY_MODIFIED_TAB,
                    label: RECENTLY_MODIFIED_TAB,
                    dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID
                  },
                  { value: ALL_TAB, label: ALL_TAB, dataTestId: "allApisTab" }
                ]}
                value={this.getActiveTab()}
                onChange={(value: string) => this.setState({ pickedTab: value })}
                dataTestId="apiBuilderTabs"
              />
            }
            search={
              <KuikaInput
                size="md"
                placeholder="Search by name..."
                value={this.state.searchText}
                leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                  this.handleSearchChange(String(value))
                }
                data-testid="searchApiBuilderInput"
                data-onboarding-id="searchable-panel-header-api's"
                // The panel header this replaced offered one-click clearing; the toolbar
                // input keeps it rather than making the user select and delete.
                rightIcon={
                  this.state.searchText ? (
                    <button
                      type="button"
                      className="kuika-api-builder__search-clear"
                      aria-label="Clear search"
                      data-testid="clearSearchInputButton"
                      onClick={() => this.handleSearchChange("")}
                    >
                      <KuikaSvg name="x" width={12} height={12} />
                    </button>
                  ) : undefined
                }
              />
            }
          />

          {this.renderTable()}
        </KuikaListPage>

        <ApiNameModal
          visible={this.state.isNameModalVisible}
          title="Create a blank API"
          subtitle="Name the API, then add the methods it exposes."
          confirmLabel="Create"
          status={this.props.createStatus}
          apis={this.getApis()}
          reservedKeys={this.props.sd?.project?.commonReservedKeys ?? []}
          onCancel={this.closeNameModal}
          onSubmit={this.handleNameSubmit}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const apiBuilderLanding = withSD<IApiBuilderLandingProps>(ApiBuilderLanding);
export { apiBuilderLanding as ApiBuilderLanding };
